import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { DEFAULT_PAGE_SIZE, pascalToCamel, urlEndPoint } from "helpers/GeneralHelper";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useOnboardingContext } from "libs/context/OnboardingContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import { submitOnboardingFilterData } from "model/OnboardingModel";
import Loaders from "components/Common/Loaders";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const OnBoardingList = () => {
    const { fetchOnboardingContextData, onboardingContextData } = useOnboardingContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Onboarding");
    const initialFilterFetchedRef = useRef(false);
    const { permissionData } = usePermissionContext() || {};
    const navigate = useNavigate();
    const [modal1, setModal1] = useState(false);
    const toggleViewModal = () => setModal1(!modal1);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterForm, setFilterForm] = useState({
      search: "",
      fromDate: "",
      toDate: "",
    });
    const [onboardingData, setOnboardingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalPages: 0,
      totalRecords: 0,
    });
    const onboardingActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.OnboardingAddAccess),
            canEdit: Boolean(permissionData?.OnboardingAddAccess),
            canView: Boolean(
                permissionData?.OnboardingAccess && !permissionData?.OnboardingAddAccess
            ),
        }),
        [permissionData]
    );

    const hasAnyOnboardingRowAction = useMemo(
        () =>
            onboardingActionPermissions.canEdit || onboardingActionPermissions.canView,
        [onboardingActionPermissions]
    );

    // 🔍 Filter only when search button clicked (uses searchQuery)
    const filteredData = useMemo(() => {
        return onboardingData;
    }, [onboardingData]);

    const formatBillDateToMMDDYYYY = (value) => {
      if (!value) return "";
      const parsedDate = dayjs(value);
      if (!parsedDate.isValid()) return value;
      return parsedDate.format("MM-DD-YYYY");
    };

    useEffect(() => {
      fetchOnboardingContextData?.();
    }, []);

    useEffect(() => {
      if (!settingsReady || initialFilterFetchedRef.current) return;
      initialFilterFetchedRef.current = true;
      const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
      setRequestPayload((previous) => ({ ...previous, pageSize }));
      setPaginationMeta((previous) => ({ ...previous, pageSize }));
      getOnboardingList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    useEffect(() => {
      const valueReModified = onboardingContextData?.table?.columns?.map((col) => ({
        Header: col?.title,
        accessor: pascalToCamel(col?.field),
        type: col?.type,
      }));
      setTableMetaData(valueReModified || []);
    }, [onboardingContextData]);

    const getOnboardingList = async (payloadOverrides = {}) => {
      setLoading(true);
      const payload = {
        ...requestPayload,
        ...payloadOverrides,
      };
      const response = await submitOnboardingFilterData(payload);
      const responseData = response?.data || {};
      const listData = responseData?.records || [];
      const valueReModified = listData?.map((item = {}) => ({
        ...item,
        id: item?.id || item?._id || "",
        type: item?.type || "",
        client: item?.client || item?.clientName || "",
        source: item?.source || "",
        billDate: formatBillDateToMMDDYYYY(item?.billDate || item?.bill_date || ""),
        plan: item?.plan || "",
        safety: item?.safety || "",
        addSpend: item?.addSpend || item?.add_spend || "",
        approved: item?.approved || "",
        status: item?.status || "",
      }));

      if (response) {
        setOnboardingData(valueReModified || []);
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || 0,
        });
        setRequestPayload(payload);
      } else {
        setOnboardingData([]);
      }
      setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
      await getOnboardingList({
        ...data,
        search: requestPayload?.search,
        pageNumber: 1,
      });
      setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value, extraFilters = {}) => {
      const trimmedValue = value?.trim() || "";
      const nextPayload = {
        ...requestPayload,
        pageNumber: 1,
        search: trimmedValue,
        name: trimmedValue,
        fromDate: extraFilters?.fromDate || null,
        toDate: extraFilters?.toDate || null,
      };
      if (!trimmedValue) {
        delete nextPayload.search;
        delete nextPayload.name;
      }
      await getOnboardingList(nextPayload);
    };

    const handleFilterFieldChange = (e) => {
      const { name, value } = e.target;
      setFilterForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handlePageChange = async (page) => {
      await getOnboardingList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
      await getOnboardingList({ pageNumber: 1, pageSize: rows });
    };

    const handleEdit = (item = {}) => {
      const itemId = item?.id || item?._id || "";
      if (!itemId) return;
      setSelectedId(itemId);
      navigate(`/${urlEndPoint.addEditOnboarding}`, {
        state: { itemId },
      });
    };

    const handleView = (item = {}) => {
      const itemId = item?.id || item?._id || "";
      if (!itemId) return;
      navigate(`/${urlEndPoint.addEditOnboarding}`, {
        state: { itemId, viewMode: true },
      });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* View Modal */}
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

                    <Card>
                        <CardBody>
                            <Col xs="12" md="12" className="d-flex justify-content-between align-items-center mt-2">
                            <h5 className="fw-bold mb-0">Onboarding List</h5>
                                {onboardingActionPermissions.canCreate ? (
                                  <Button
                                    color="primary"
                                    onClick={() => navigate(`/${urlEndPoint.addEditOnboarding}`)}
                                  >
                                    + Create
                                  </Button>
                                ) : null}
                              </Col>
                            <hr />

                            <LegacyFilterToolbar>
                            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                              <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                                <span className="manage-user-filter-label">Search</span>
                                <div className="metadata-filter-field__control">
                                <Input
                                  type="text"
                                  name="search"
                                  placeholder="Search by Email/Company etc."
                                  className={LEGACY_FILTER_INPUT_CLASS}
                                  value={filterForm.search}
                                  onChange={handleFilterFieldChange}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      setSearchQuery(filterForm.search);
                                      handleSearchSubmit(filterForm.search, filterForm);
                                    }
                                  }}
                                />
                                </div>
                              </Col>
                              <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--date">
                                <span className="manage-user-filter-label">From date</span>
                                <div className="metadata-filter-field__control">
                                <Input
                                  type="date"
                                  name="fromDate"
                                  value={filterForm.fromDate}
                                  onChange={handleFilterFieldChange}
                                  className={LEGACY_FILTER_INPUT_CLASS}
                                />
                                </div>
                              </Col>
                              <Col xs="12" md="3" className="metadata-filter-field metadata-filter-field--date">
                                <span className="manage-user-filter-label">To date</span>
                                <div className="metadata-filter-field__control">
                                <Input
                                  type="date"
                                  name="toDate"
                                  value={filterForm.toDate}
                                  onChange={handleFilterFieldChange}
                                  className={LEGACY_FILTER_INPUT_CLASS}
                                />
                                </div>
                              </Col>
                              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                                <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                                <div className="metadata-filter-toolbar__actions">
                                <Button
                                  color="primary"
                                  className="w-100 manage-user-search-btn"
                                  onClick={() => {
                                    setSearchQuery(filterForm.search);
                                    handleSearchSubmit(filterForm.search, filterForm);
                                  }}
                                >
                                  <i className="fa fa-search me-1"></i>
                                  Search
                                </Button>
                                </div>
                              </Col>
                              
                            </Row>
                            </LegacyFilterToolbar>

                            {!loading && onboardingData?.length && tableMetaData?.length ? (
                              <TableContainer
                                columns={tableMetaData}
                                data={filteredData}
                                isActionRequired={hasAnyOnboardingRowAction}
                                isCheckboxLeftSide={false}
                                isPagination={true}
                                currentPage={paginationMeta?.pageNumber}
                                rowsPerPage={paginationMeta?.pageSize}
                                totalPages={paginationMeta?.totalPages}
                                totalRecords={paginationMeta?.totalRecords}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handleRowsPerPageChange}
                                setCommonFilterModalOpen={setCommonFilterModalOpen}
                                pageName={"Onboarding"}
                                onEdit={
                                  onboardingActionPermissions.canEdit
                                    ? handleEdit
                                    : undefined
                                }
                                // onView={
                                //   onboardingActionPermissions.canView
                                //     ? handleView
                                //     : undefined
                                // }
                              />
                            ) : (
                              // <Card className="border mb-3">
                              //   <CardBody>
                              //     <Spinners setLoading={setLoading} />
                              //   </CardBody>
                              // </Card>
                              <Loaders />
                            )}
                        </CardBody>
                    </Card>
                    <AllCommonFilterModal
                      setCommonFilterModalOpen={setCommonFilterModalOpen}
                      commonFilterModalOpen={commonFilterModalOpen}
                      modalTitle="Onboarding"
                      handleSubmit={handleFilterSubmit}
                      filterData={onboardingContextData?.filters || []}
                      selectedId={selectedId}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

OnBoardingList.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(OnBoardingList);
